/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable indent */

import { useQueryClient } from '@tanstack/react-query';
import sign from 'jwt-encode';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import EpButtonVision from '../../../components/EpButtonVision';
import { EpHeaderNavigation } from '../../../components/EpHeaderNavigation';
import { EpLoading } from '../../../components/EpLoading';
import { hideModal, showModal } from '../../../components/EpModal';
import { EpModalHelpSupport } from '../../../components/EpModalHelpSupport';
import { EpModalTimeout } from '../../../components/EpModalTimeout';
import { MODAL_TIMEOUT_ID } from '../../../constants/authConstants';
import {
  ROUTE_CLASSIFICATION_NOTIFICATION_CLUESTERED_MANAGEMENT,
  ROUTE_CLASSIFICATION_NOTIFICATION_MANAGEMENT,
  ROUTE_HOMEPAGE,
  ROUTE_INCIDENT_NOTIFICATION_SEARCH,
  ROUTE_MESSAGE,
  ROUTE_PHARMACY_HOMEPAGE,
  ROUTE_PROFESSIONAL,
  ROUTE_UNIT,
  ROUTE_USER,
  ROUTE_USER_DATA,
} from '../../../constants/urlConstants';
import { useBundles } from '../../../hooks/useBundles';
import { useTranslation as t } from '../../../hooks/useTranslation';
import { IconNamesByRoleEnum } from '../../../interfaces';
import PatientSafetySealModal from '../../../layout/PatientSafetySealModal';
import {
  signinSilent,
  signoutRedirect,
  userManager,
} from '../../../services/authService';
import { stampsService } from '../../../services/stampsService';
import { userService } from '../../../services/userService';
import { useTranslationStore } from '../../../stores';
import { useAuthStore } from '../../../stores/useAuthStore';
import { transformToReactIcons } from '../../../utils';
import { getUser } from '../../../utils/auth';
import { takeCorrectBundle } from '../../../utils/bundle';
import { getDefaultAccess } from '../../../utils/defaultAccess';
import {
  alertsRole,
  dashboardManageRole,
  investigatorRole,
  investigatorTeamRole,
  leadershipRole,
  notifierPharmacyRole,
  notifierRole,
  observerRole,
  patientSafetyRole,
  pharmacyManagerRole,
} from '../../../utils/roles';
import styles from '../EpStarter.module.scss';
import { StarterModal } from '../EpStarterModal';
import { User } from 'oidc-client-ts';
import { useGlobalMenuStore } from '../../../stores/useGlobalMenuStore';
import { useState } from 'react';
import {
  formatMenuButtons,
  getPrimaryMenuFormatted,
  getSecondaryMenuItemsFromPrimary,
} from '../../../utils/globalNavigation';

const { getUserBundles } = userService;

export const InternalHeader = ({ cultures }: { cultures: any }) => {
  const {
    i18n: { language, changeLanguage },
  } = useTranslation();
  const { translations } = useTranslationStore((state) => state);
  const { openModalTimeout, setOpenModalTimeout, isActivity } = useAuthStore();
  const userInfo = getUser();
  const hospitalInfo = getDefaultAccess();
  const router = useRouter();
  const pathname = usePathname();
  const { getHospitalLogo, getUserApps } = userService;
  const { data: hospitalLogo = '' } = getHospitalLogo(hospitalInfo?.hospitalId);
  const { data: userApps = [] } = getUserApps();
  const { setShowSecondaryMenu, userHasAssessmentRole } = useGlobalMenuStore();
  const roleId = getDefaultAccess()?.roleId.toLowerCase();

  const manageUrlLink = process.env.NEXT_PUBLIC_MANAGE_ACCOUNT_SSO;
  const hospitalLogoUrl = hospitalLogo?.filePath ? hospitalLogo?.filePath : '';
  const modalHelpSupportId = 'header-modal-help-support';
  const modalCulturesId = 'ep-modal__language';
  const shouldActiveNotificationMenu =
    pathname === ROUTE_HOMEPAGE ||
    pathname === ROUTE_PHARMACY_HOMEPAGE ||
    pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH;

  const [isConfigMenuActive, setIsConfigMenuActive] = useState(false);
  const [isNotificationMenuActive, setIsNotificationMenuActive] = useState(
    shouldActiveNotificationMenu,
  );

  const bundleKey = [
    'userBundles',
    language,
    hospitalInfo?.tenantId,
    hospitalInfo?.networkId,
    hospitalInfo?.hospitalId,
  ];
  const userBundles: any = useQueryClient().getQueryData(bundleKey);
  const { isFetching: isLoadingBundles } = getUserBundles({
    hospitalId: hospitalInfo?.hospitalId,
    enabled: !!hospitalInfo && !userBundles,
    key: bundleKey,
  });
  const hasStandardBundle = useBundles(
    Number(process.env.NEXT_PUBLIC_BUNDLES_STANDARD),
  );
  const hasPharmacyBundle = useBundles(
    Number(process.env.NEXT_PUBLIC_BUNDLES_PHARMACY),
  );
  const hasACSPBundle = useBundles(
    Number(process.env.NEXT_PUBLIC_BUNDLES_ACSP),
  );

  const shouldShowAssementShortcut = hasACSPBundle && userHasAssessmentRole;

  const { getCurrentStamp, GetViewStamp } = stampsService;

  const { data: stamp, isFetching: isLoadingStamp } = getCurrentStamp({
    hospitalId: hospitalInfo?.hospitalId || '',
    categoryId: process.env.NEXT_PUBLIC_PATIENTY_SAFETY_STAMP_CATEGORY_ID || '',
  });

  const { data: viewStamp } = GetViewStamp({
    hospitalId: hospitalInfo?.hospitalId || '',
    categoryId: process.env.NEXT_PUBLIC_PATIENTY_SAFETY_STAMP_CATEGORY_ID || '',
    enabled: !!stamp,
  });

  const DoCheckTokenExpiration = async () => {
    try {
      const user: User | null = await userManager.getUser();

      if (user && user.expires_at) {
        const currentTime = Math.floor(Date.now() / 1000); // Tempo atual em segundos
        const hasValidExpirationTimeToken =
          user.expires_at - currentTime <= 60 &&
          user.expires_at - currentTime > 0;

        // Verifica se o token está próximo de expirar (por exemplo, 1 minuto antes)
        if (hasValidExpirationTimeToken && isActivity) {
          await signinSilent(); // Renova o token silenciosamente
        }

        if (user.expires_at - currentTime < 0) {
          setOpenModalTimeout(true);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const productsPopoverListData = () => {
    return (
      userApps &&
      userApps.map((app: any) => ({
        accessUrl: app?.accessUrl, //'http://localhost:3002/'
        allowedForUser: app?.allowedForUser,
        displayOrder: app?.displayOrder,
        iconComponentName: transformToReactIcons(app?.iconComponentName),
        id: app?.id,
        longDescription: app?.longDescription,
        longName: app?.longName,
        moreInfoUrl: app?.moreInfoUrl,
        shortDescription: app?.shortDescription,
        shortName: app?.shortName,
      }))
    );
  };

  const avatarUserMenuDropdownDefaultListData = {
    manageAccount: {
      id: 'header-navigation__btn-manage-account--02',
      buttonOnClickFunc: () => window.open(manageUrlLink || '', '_ blank'),
    },
    language: {
      id: 'header-navigation__btn-lang--02',
      buttonOnClickFunc: () => showModal(modalCulturesId),
    },
    support: {
      id: 'header-navigation__btn-support-02',
      buttonOnClickFunc: () => showModal(modalHelpSupportId),
    },
    knowledgeBase: {
      id: 'header-navigation__btn-knowledge-base--02',
      buttonOnClickFunc: () => console.log(`knowledge base`), // @TODO - falta link
    },
    exit: {
      id: 'header-navigation__btn-exit--02',
      buttonOnClickFunc: () => signoutRedirect(true),
    },
  };

  const translationsData = {
    epimedSystems: t(translations['global'], 'Epimed Systens'),
    epimedApps: t(translations['global'], 'Epimed Apps'),
    cancel: t(translations['global'], 'Cancel'),
    learnMore: t(translations['global'], 'Learn More'),
    manageAccountButtonText: t(
      translations['global'],
      'Manage your Epimed account',
    ),
    languageButtonText: t(translations['global'], 'Language ({0})').replace(
      '{0}',
      language,
    ),
    supportButtonText: t(translations['global'], 'Support'),
    knowledgeBaseButtonText: t(translations['global'], 'Knowledge Base'),
    exitBaseButtonText: t(translations['global'], 'Exit'),
    prodigeTitle: t(translations['global'], 'Prodige'),
    prodigeText: (
      <span>
        <p>
          {t(
            translations['global'],
            'Individual development program of efficient management.',
          )}
        </p>
        <p>
          {t(
            translations['global'],
            'Critical analysis of clinical indicators.',
          )}
        </p>
      </span>
    ),
  };

  const jwtToken = sign(hospitalInfo, '');
  const takeUrlNavToVite = (route: string) =>
    `${process.env.NEXT_PUBLIC_BASE_SITE_URL_SP_VITE}${route}/?tsa=${jwtToken}&CultureCode=${language}`;

  const takeProductNavigationListData = () => {
    const notificationButton = {
      id: 'notification',
      buttonId: 'notification',
      buttonText: t(translations['global'], 'Notification'),
      buttonIsActive: isNotificationMenuActive,
      buttonOnClickFunc: () => {
        setIsNotificationMenuActive(true);
        if (isConfigMenuActive) setIsConfigMenuActive(false);
      },
    };

    const notificationNotifierMenu: any[] = [
      {
        id: 1,
        buttonId: 'notificationNotifierMenu',
        buttonText: t(translations['global'], 'Incident Notification'),
        relativeParentId: null,
        buttonIsActive:
          pathname === ROUTE_HOMEPAGE ||
          pathname === ROUTE_PHARMACY_HOMEPAGE ||
          pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
      {
        id: 2,
        buttonId: 'button--2',
        buttonText: t(translations['global'], 'Incident Notification Form'),
        buttonOnClickFunc: () => {
          router.push(ROUTE_HOMEPAGE);
          setShowSecondaryMenu(true);
        },
        relativeParentId: 'notificationNotifierMenu',
        buttonIsActive: pathname === ROUTE_HOMEPAGE,
      },
      {
        id: 4,
        buttonId: 'button--4',
        buttonText: t(
          translations['global'],
          'Check status of incident notification',
        ),
        buttonOnClickFunc: () => {
          router.push(ROUTE_INCIDENT_NOTIFICATION_SEARCH);
          setShowSecondaryMenu(true);
        },
        relativeParentId: 'notificationNotifierMenu',
        buttonIsActive: pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
    ];

    const pharmacyConfigurations = [
      {
        id: 1,
        buttonId: 'pharmacyConfigurations',
        buttonText: t(translations['global'], 'Configurations'),
        buttonIsActive: isConfigMenuActive,
        relativeParentId: 'notificationPharmacyMenu',
        buttonOnClickFunc: () => {
          setIsNotificationMenuActive(false);
          setIsConfigMenuActive(true);
        },
      },
      {
        id: 15,
        buttonId: 'button--8',
        buttonText: t(translations['global'], 'Pharmacy contact details'),
        buttonOnClickFunc: () => {
          router.push(takeUrlNavToVite(ROUTE_USER_DATA));
        },
        relativeParentId: 'pharmacyConfigurations',
      },
    ];

    const notificationPharmacyMenu: any[] = [
      {
        id: 1,
        buttonId: 'notificationPharmacyMenu',
        buttonText: t(translations['global'], 'Incident Notification'),
        relativeParentId: null,
        buttonIsActive:
          pathname === ROUTE_HOMEPAGE ||
          pathname === ROUTE_PHARMACY_HOMEPAGE ||
          pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
      {
        ...notificationButton,
        relativeParentId: 'notificationPharmacyMenu',
      },
      {
        id: 2,
        buttonId: 'button--2',
        buttonText: t(translations['global'], 'Incident Notification Form'),
        buttonOnClickFunc: () => {
          setShowSecondaryMenu(true);
          router.push(ROUTE_HOMEPAGE);
        },
        relativeParentId: 'notification',
        buttonIsActive: pathname === ROUTE_HOMEPAGE,
      },
      {
        id: 3,
        buttonId: 'button--3',
        buttonText: t(translations['global'], 'Clinical Pharmacy Form'),
        buttonOnClickFunc: () => {
          setShowSecondaryMenu(true);
          router.push(ROUTE_PHARMACY_HOMEPAGE);
        },
        relativeParentId: 'notification',
        buttonIsActive: pathname === ROUTE_PHARMACY_HOMEPAGE,
      },
      {
        id: 4,
        buttonId: 'button--4',
        buttonText: t(
          translations['global'],
          'Check status of incident notification',
        ),
        buttonOnClickFunc: () => {
          setShowSecondaryMenu(true);
          router.push(ROUTE_INCIDENT_NOTIFICATION_SEARCH);
        },
        relativeParentId: 'notification',
        buttonIsActive: pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
    ];

    const clinicalPharmacyForm: any = {
      id: 13,
      buttonId: 'clinicalPharmacyForm',
      buttonText: t(translations['global'], 'Clinical Pharmacy Form'),
      buttonOnClickFunc: () => {
        router.push(ROUTE_PHARMACY_HOMEPAGE);
        setShowSecondaryMenu(true);
      },
      relativeParentId: 'notificationPharmacyMenu',
      buttonIsActive: pathname === ROUTE_PHARMACY_HOMEPAGE,
    };

    const notificationPharmacyMenuOnlyBundle: any[] = [
      {
        id: 12,
        buttonId: 'notificationPharmacyMenu',
        buttonText: t(translations['global'], 'Incident Notification'),
        relativeParentId: null,
        buttonIsActive:
          pathname === ROUTE_HOMEPAGE ||
          pathname === ROUTE_PHARMACY_HOMEPAGE ||
          pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
      clinicalPharmacyForm,
    ];

    const monitorMenu: any[] = [
      {
        id: 5,
        buttonId: 'NotificationsManagement',
        buttonText: t(translations['global'], 'Status'),
        buttonOnClickFunc: () => {
          router.push(
            takeUrlNavToVite(ROUTE_CLASSIFICATION_NOTIFICATION_MANAGEMENT),
          );
          setShowSecondaryMenu(true);
        },
        relativeParentId: 'incidentNotifierMenu',
      },
    ];

    const messageTemplateMenu: any = {
      id: 9,
      buttonId: 'button--7',
      buttonText: t(translations['global'], 'Message template'),
      buttonOnClickFunc: () => router.push(takeUrlNavToVite(ROUTE_MESSAGE)),
      relativeParentId: 'Configurations',
    };

    const institutionLinkMenu: any = {
      id: 10,
      buttonId: 'button--8',
      buttonText: t(translations['global'], 'Hospital contact details'),
      buttonOnClickFunc: () => router.push(takeUrlNavToVite(ROUTE_USER_DATA)),
      relativeParentId: 'Configurations',
    };
    const cluesteredNotificationMenu: any[] = [
      {
        id: 11,
        buttonId: 'ClusteredNotifications',
        buttonText: t(translations['global'], 'Grouped Notification'),
        buttonOnClickFunc: () => {
          router.push(
            takeUrlNavToVite(
              ROUTE_CLASSIFICATION_NOTIFICATION_CLUESTERED_MANAGEMENT,
            ),
          );
          setShowSecondaryMenu(true);
        },
        relativeParentId: 'incidentNotifierMenu',
      },
    ];

    const configMenuItems: any = [
      {
        id: 7,
        buttonId: 'button--5',
        buttonText: t(translations['global'], 'Units'),
        buttonOnClickFunc: () => router.push(takeUrlNavToVite(ROUTE_UNIT)),
        relativeParentId: 'Configurations',
      },
      {
        id: 8,
        buttonId: 'button--6',
        buttonText: t(translations['global'], 'Users'),
        buttonOnClickFunc: () => router.push(takeUrlNavToVite(ROUTE_USER)),
        relativeParentId: 'Configurations',
      },
      {
        id: 9,
        buttonId: 'button--professional',
        buttonText: t(translations['global'], 'Professionals'),
        buttonOnClickFunc: () =>
          router.push(takeUrlNavToVite(ROUTE_PROFESSIONAL)),
        relativeParentId: 'Configurations',
      },
      institutionLinkMenu,
      messageTemplateMenu,
    ];

    const configMenu: any[] = [
      {
        id: 6,
        buttonId: 'Configurations',
        buttonText: t(translations['global'], 'Configurations'),
        relativeParentId: 'incidentNotifierMenu',
        buttonOnClickFunc: () => {
          setIsNotificationMenuActive(false);
          setIsConfigMenuActive(true);
          setShowSecondaryMenu(true);
        },
        buttonIsActive: isConfigMenuActive,
      },
      ...configMenuItems,
    ];

    const configMenuPharmacyContactDetails: any[] = [
      {
        id: 6,
        buttonId: 'Configurations',
        buttonText: t(translations['global'], 'Configurations'),
        relativeParentId: 'incidentNotifierMenu',
        buttonOnClickFunc: () => {
          setShowSecondaryMenu(true);
        },
      },
      ...configMenuItems,
    ];

    const assessmentMenu: any = {
      id: 'assessmentMenu',
      buttonId: 'assessmentMenu',
      buttonText: t(translations['global'], 'Assessment'),
      buttonIsActive: false,
      buttonOnClickFunc: () => {
        setShowSecondaryMenu(false);
        router.push(process.env.NEXT_PUBLIC_ASSESSMENT as string);
      },
    };

    const incidentNotifierMenu: any[] = [
      {
        id: 'incidentNotifierMenu',
        buttonId: 'incidentNotifierMenu',
        buttonText: t(translations['global'], 'Incident Notification'),
        relativeParentId: null,
        buttonIsActive:
          pathname === ROUTE_HOMEPAGE ||
          pathname === ROUTE_PHARMACY_HOMEPAGE ||
          pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
      {
        ...notificationButton,
        relativeParentId: 'incidentNotifierMenu',
      },
      {
        id: 2,
        buttonId: 'button--2',
        buttonText: t(translations['global'], 'Incident Notification Form'),
        buttonOnClickFunc: () => router.push(ROUTE_HOMEPAGE),
        relativeParentId: 'notification',
        buttonIsActive: pathname === ROUTE_HOMEPAGE,
      },
      {
        id: 4,
        buttonId: 'button--4',
        buttonText: t(
          translations['global'],
          'Check status of incident notification',
        ),
        buttonOnClickFunc: () => {
          router.push(ROUTE_INCIDENT_NOTIFICATION_SEARCH);
        },
        relativeParentId: 'notification',
        buttonIsActive: pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
    ];

    const dashboardAndReportsMenu = [
      {
        id: 'incidentNotifierMenu',
        buttonId: 'incidentNotifierMenu',
        buttonText: t(translations['global'], 'Incident Notification'),
        relativeParentId: null,
        buttonIsActive:
          pathname === ROUTE_HOMEPAGE ||
          pathname === ROUTE_PHARMACY_HOMEPAGE ||
          pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
      {
        ...notificationButton,
        relativeParentId: 'incidentNotifierMenu',
      },
      {
        id: 2,
        buttonId: 'button--2',
        buttonText: t(translations['global'], 'Incident Notification Form'),
        buttonOnClickFunc: () => router.push(ROUTE_HOMEPAGE),
        relativeParentId: 'notification',
        buttonIsActive: pathname === ROUTE_HOMEPAGE,
      },
      {
        id: 4,
        buttonId: 'button--4',
        buttonText: t(
          translations['global'],
          'Check status of incident notification',
        ),
        buttonOnClickFunc: () =>
          router.push(ROUTE_INCIDENT_NOTIFICATION_SEARCH),
        relativeParentId: 'notification',
        buttonIsActive: pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH,
      },
    ];

    let menuArr = [];
    const secondaryMenu = { items: [] } as any;
    const isPharmacy = { value: false };

    switch (roleId) {
      case patientSafetyRole:
        if (hasStandardBundle) {
          const items: any[] = [
            {
              ...cluesteredNotificationMenu[0],
              relativeParentId: 'incidentNotifierMenu',
            } as any,
            configMenu,
          ];

          menuArr = [
            {
              ...monitorMenu[0],
              relativeParentId: 'incidentNotifierMenu',
            },
            incidentNotifierMenu,

            ...items,
          ];
        } else {
          menuArr = [
            {
              ...monitorMenu[0],
              relativeParentId: 'incidentNotifierMenu',
            },
            incidentNotifierMenu,
            configMenu,
          ];
        }
        break;

      case investigatorRole:
      case observerRole:
        menuArr = [monitorMenu, incidentNotifierMenu];
        break;

      case notifierRole:
      case alertsRole:
      case investigatorTeamRole:
      case leadershipRole:
      case dashboardManageRole:
        menuArr = dashboardAndReportsMenu;
        break;

      //Notifier User Pharmacy Clinic
      case notifierPharmacyRole:
        if (
          userBundles?.length === 1 &&
          takeCorrectBundle(
            userBundles,
            Number(process.env.NEXT_PUBLIC_BUNDLES_PHARMACY),
          )
        ) {
          isPharmacy.value = true;
          secondaryMenu.items = [clinicalPharmacyForm];
          menuArr = [notificationPharmacyMenuOnlyBundle];
        } else if (!hasPharmacyBundle) {
          menuArr = [notificationNotifierMenu];
        } else {
          menuArr = [notificationPharmacyMenu];
        }

        break;

      case pharmacyManagerRole:
        if (
          userBundles?.length === 1 &&
          takeCorrectBundle(
            userBundles,
            Number(process.env.NEXT_PUBLIC_BUNDLES_PHARMACY),
          )
        ) {
          isPharmacy.value = true;

          menuArr = [...notificationPharmacyMenu, ...pharmacyConfigurations];
        } else {
          isPharmacy.value = true;
          menuArr = [...notificationPharmacyMenu, ...pharmacyConfigurations];
        }

        break;

      default:
        menuArr = [incidentNotifierMenu];
        break;
    }

    if (shouldShowAssementShortcut && !isPharmacy.value) {
      menuArr.push(assessmentMenu);
    }

    // Coletar todos os itens de menu em formato plano para processamento com a nova estrutura
    // Isso inclui todos os itens de todos os menus, independentemente da estrutura
    const allMenuItemsFlat = menuArr.flatMap((menuItem: any) => {
      // Se for um objeto com buttonGroupArray (estrutura antiga)
      if (
        menuItem?.buttonGroupArray &&
        Array.isArray(menuItem.buttonGroupArray)
      ) {
        return menuItem.buttonGroupArray;
      }
      // Se for um array diretamente (caso especial do pharmacyManagerRole)
      if (Array.isArray(menuItem)) {
        return menuItem;
      }
      // Se for um item simples (como assessmentMenu ou pharmacyContactDetails)
      if (menuItem?.buttonId) {
        return [menuItem];
      }
      return [];
    });

    const formattedPrimaryMenuResult = getPrimaryMenuFormatted({
      menu: allMenuItemsFlat,
    });

    const formattedPrimaryMenu = Array.isArray(formattedPrimaryMenuResult)
      ? formattedPrimaryMenuResult
      : [];

    const secondaryMenuItemsFromPrimary = getSecondaryMenuItemsFromPrimary(
      formattedPrimaryMenu,
      allMenuItemsFlat,
    );

    const allSecondaryMenuItems = [
      ...secondaryMenu.items,
      ...configMenuItems,
      ...allMenuItemsFlat,
    ];

    const formattedSecondaryMenuResult = formatMenuButtons({
      items: secondaryMenu.items,
      dropdownItems: configMenuItems,
      relativeParentId: 'Configurations',
      allMenuItems: allSecondaryMenuItems,
    });

    const formattedSecondaryMenu = Array.isArray(formattedSecondaryMenuResult)
      ? formattedSecondaryMenuResult
      : formattedSecondaryMenuResult?.items || [];

    const combinedSecondaryMenu = [
      ...(Array.isArray(formattedSecondaryMenu) ? formattedSecondaryMenu : []),
      ...secondaryMenuItemsFromPrimary,
    ];

    return {
      primaryMenu: formattedPrimaryMenu,
      secondaryMenu: combinedSecondaryMenu,
      initiallyActiveItemId: (pathname === ROUTE_HOMEPAGE ||
        pathname === ROUTE_PHARMACY_HOMEPAGE ||
        pathname === ROUTE_INCIDENT_NOTIFICATION_SEARCH) as any,
    };
  };

  useEffect(() => {
    if (openModalTimeout) {
      showModal(MODAL_TIMEOUT_ID);
    }
  }, [openModalTimeout]);

  useEffect(() => {
    const AUTHORIZED_ROUTE_TO_SHOW_SEAL = 'PSNotification';
    const isAuthorizedRouteToShowStamp =
      pathname.endsWith(AUTHORIZED_ROUTE_TO_SHOW_SEAL) ||
      pathname.endsWith(`${AUTHORIZED_ROUTE_TO_SHOW_SEAL}/`);

    const shouldShowSealModal =
      !!stamp && viewStamp === null && isAuthorizedRouteToShowStamp;

    if (shouldShowSealModal) {
      showModal('modal--sp-seal-id');
    }
  }, [stamp, viewStamp, pathname]);

  useEffect(() => {
    // Chame a função DoCheckTokenExpiration em intervalos para verificar a expiração do token
    const intervalId = setInterval(DoCheckTokenExpiration, 1 * 60 * 1000); // Verifica a cada 1 minuto

    return () => clearInterval(intervalId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isConfigMenuActive && !pathname) setIsConfigMenuActive(false);
  }, [pathname, isNotificationMenuActive, isConfigMenuActive]);

  if (isLoadingBundles) {
    return <EpLoading loadingId={'loading--page'} isVisible />;
  }

  const headerNavigationMenuData = takeProductNavigationListData();

  return (
    <>
      <EpHeaderNavigation
        customClasses={styles['header__nav']}
        headerNavigationSystemTitle={t(
          translations['global'],
          'Patient Safety',
        )}
        headerNavigationButtonId={'headerNavigationTest'}
        headerNavigationProductsPopoverId={'headerNavigationTestPopover'}
        headerNavigationProductsHostPopoverId={
          'headerNavigationTestPopoverHost'
        }
        headerNavigationHospitalLogoSrc={hospitalLogoUrl}
        userEmail={userInfo?.email}
        userName={userInfo?.fullname}
        userRole={hospitalInfo?.hospital?.role}
        currentSystemLanguage={language}
        productsPopoverList={productsPopoverListData()}
        avatarUserMenuDropdownDefaultList={
          avatarUserMenuDropdownDefaultListData
        }
        translations={translationsData}
        homeButtonId={'headerNavigationTestHome'}
        homeButtonHref={ROUTE_HOMEPAGE}
        /** @TODO - Prodige comentado devido a tarefa #101359 */
        // prodigeButtonHref={'https://epimed.medportal.com.br/login'}
        headerNavigationMenuData={headerNavigationMenuData}
        buttonVisionContent={
          <EpButtonVision
            buttonVisionId={'btn-vision'}
            buttonVisionPopoverProps={{
              id: 'btn-vsn',
              hostId: 'btn-vsn-host',
              avatarId: 'avtr-btn-vsn',
              triggerText: hospitalInfo?.hospital?.name,
              triggerAvatarId: 'avtr-trgr',
              institutionName: hospitalInfo?.hospital?.name,
              role: hospitalInfo?.hospital?.role,
              iconNameByRole: IconNamesByRoleEnum['hospital-manager'],
            }}
            buttonOnClickFunc={() => router.push('/SystemAccess')}
          />
        }
      />

      {!isLoadingStamp && stamp && <PatientSafetySealModal stamp={stamp} viewStamp={viewStamp} />}

      {openModalTimeout && (
        <EpModalTimeout
          modalId={MODAL_TIMEOUT_ID}
          translations={translations['global']}
        />
      )}

      <EpModalHelpSupport
        modalId={modalHelpSupportId}
        translations={translations['global']}
      />

      <StarterModal
        cultures={cultures}
        handleLanguage={(value: string) => changeLanguage(value)}
        translations={translations['global']}
      />
    </>
  );
};
